import React, { useState, useEffect, useMemo } from "react";
import { TextInputField } from "../../bootstrap/TextInputField";
import { NumberInputField } from "../../bootstrap/NumberInputField";
import { Row, Col, Form, Accordion } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import ToolTip from "../../bootstrap/ToolTip";
import { FaExclamationCircle } from "react-icons/fa";
import IntInputField from "../../bootstrap/IntInputField";
import DecimalInputField from "../../bootstrap/DecimalInputField";
import { isIEFPUser } from "../../../authentication/authenticationHelper";
import { getQualificationLevels } from "../../../rest/entity";

export function EaAccreditationFormStep1({
  formStep,
  numberOfSteps,
  errors,
  handleChange,
  formikValues,
  handleNextStep,
  setFieldValue,
  districts,
  districtOptions,
  readMode = false,
  lockNonGet = false,
  allowGetEdit = false,
  syncGetWithIntervention = false,
}) {
  const intl = useIntl();
  const [entityTypeOtherOptionActive, setEntityTypeOtherOptionActive] =
    useState(false);
  const [qlOptions, setQlOptions] = useState([]);

  const entityTypeOptions = [
    "PRIVATE_WITHOUT_MONEY",
    "PRIVATE_WITH_MONEY",
    "PUBLIC",
    "LOCAL_AUTHORITY",
    "OTHER",
  ];

  const isIEFP = isIEFPUser();
  const regionalDelegationsAbbreviations = ["DN", "DC", "DL", "DA", "DG"];
  const nonGetDisabled = lockNonGet ? true : readMode;
  const getDisabled = readMode || (lockNonGet && !allowGetEdit);

  useEffect(() => {
    let mounted = true;
    getQualificationLevels().then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      const opts = list.map((x) => ({
        value: String(x.code ?? x.id ?? ""),
        label: x.description ?? String(x.code ?? ""),
      }));
      if (mounted) setQlOptions(opts);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const d2RD = useMemo(() => {
    const m = {};
    (districts || []).forEach((d) => (m[d.code] = d.regionalDelegationCode));
    return m;
  }, [districts]);

  const desiredRDSet = useMemo(() => {
    const s = new Set();
    (formikValues?.interventionDistrictCodes || []).forEach((dc) => {
      const rd = d2RD[dc];
      if (rd) s.add(rd);
    });
    return s;
  }, [formikValues?.interventionDistrictCodes, d2RD]);

  const fullGetList = formikValues.getList || [];
  const visibleGetList = fullGetList.filter((g) => g.active !== false);

  function currentRDSet() {
    return new Set(visibleGetList.map((g) => g.regionalDelegationCode));
  }

  const hasAnyGETFor = (rd) =>
    fullGetList.some((g) => {
      if (g.regionalDelegationCode !== rd) return false;
      if (g.isNew) return false;

      if (readMode) {
        return g.active !== false;
      }
      return true;
    });

  const hasLockedGETFor = (rd) =>
    fullGetList.some((g) => g.regionalDelegationCode === rd && g.locked);

  const hasNewGETFor = (rd) =>
    fullGetList.some((g) => g.regionalDelegationCode === rd && g.isNew);

  function findNewGETIndex(rd, anchorIndex) {
    return fullGetList.findIndex(
      (g) =>
        g.regionalDelegationCode === rd &&
        g.isNew === true &&
        (g.anchorIndex ?? null) === (anchorIndex ?? null)
    );
  }

  function hasNewGETForAnchor(rd, anchorIndex) {
    return findNewGETIndex(rd, anchorIndex) >= 0;
  }

  const hasReplacementGet = (get, index) => {
    if (get.isNew) return false;
    const rd = get.regionalDelegationCode;
    if (!rd) return false;

    return (formikValues.getList || []).some((other) => {
      if (!other.isNew) return false;
      if (other.regionalDelegationCode !== rd) return false;

      if (other.anchorIndex != null) {
        return other.anchorIndex === index;
      }
      return true;
    });
  };

  function addGET(regionalDelegationCode, opts = {}) {
    const newGET = {
      name: "",
      phone: "",
      mobile: "",
      email: "",
      type: "GET",
      regionalDelegationCode,
      managementExperience: false,
      qualificationLevelCode: "-1",
      locked: false,
      isNew: true,
      anchorIndex: opts.anchorIndex ?? null,
      ...opts,
    };
    const next = [...fullGetList, newGET];
    setFieldValue("getList", next);
  }

  function cancelNewGET(regionalDelegationCode, anchorIndex = null) {
    const next = fullGetList.filter(
      (g) =>
        !(
          g.regionalDelegationCode === regionalDelegationCode &&
          g.isNew === true &&
          (g.anchorIndex ?? null) === (anchorIndex ?? null)
        )
    );
    setFieldValue("getList", next);
  }

  function removeGET(regionalDelegationCode) {
    const idx = visibleGetList.findIndex(
      (g) => g.regionalDelegationCode === regionalDelegationCode && !g.locked
    );
    if (idx >= 0) {
      const toRemove = visibleGetList[idx];
      const next = fullGetList.filter((g) => g !== toRemove);
      setFieldValue("getList", next);
    }
  }

  useEffect(() => {
    if (!syncGetWithIntervention) return;
    const cur = currentRDSet();
    desiredRDSet.forEach((rd) => {
      if (!cur.has(rd)) addGET(rd);
    });
    cur.forEach((rd) => {
      if (!desiredRDSet.has(rd)) removeGET(rd);
    });
  }, [syncGetWithIntervention, desiredRDSet]);

  function entityTypeChangeHandler(e) {
    if (nonGetDisabled) return;
    if (e.target.value === "OTHER") {
      setEntityTypeOtherOptionActive(true);
    } else {
      setFieldValue("entity.profileDescription", "");
      setEntityTypeOtherOptionActive(false);
    }
    handleChange(e);
  }

  function regionalDelegationSelectionChangeHandler(e, regionalDelegationCode) {
    if (getDisabled) return;
    if (e.target.checked) {
      if (!hasNewGETFor(regionalDelegationCode)) addGET(regionalDelegationCode);
    } else {
      if (hasLockedGETFor(regionalDelegationCode)) return;
      removeGET(regionalDelegationCode);
    }
  }

  function handleChangeCae(e) {
    if (nonGetDisabled) return;
    setFieldValue("entity.cae", e.target.value);
  }

  const groupedByRD = useMemo(() => {
    const m = {};
    fullGetList.forEach((g, idx) => {
      const rd = g.regionalDelegationCode;
      if (!rd) return;
      if (!m[rd]) m[rd] = { existing: [], news: [] };
      const entry = { item: g, index: idx };
      if (g.isNew) m[rd].news.push(entry);
      else m[rd].existing.push(entry);
    });
    return m;
  }, [fullGetList]);

  return (
    <div>
      <h4 className="text-primary mb-0">
        <FormattedMessage id="eaAccreditationForm.step1.title" />
      </h4>
      <small className="text-secondary text-uppercase">
        {intl.formatMessage(
          { id: "all.step" },
          { step: formStep, numberOfSteps }
        )}
      </small>

      <Row>
        <Col md="8">
          <TextInputField
            labelId={"eaAccreditationForm.step1.description"}
            name={"entity.description"}
            handleChange={handleChange}
            value={formikValues.entity.description}
            isInvalid={errors.entity?.description}
            errorMessage={errors.entity?.description}
            disabled={nonGetDisabled}
          />
        </Col>
        <Col md="4">
          <NumberInputField
            labelId={"eaAccreditationForm.step1.nif"}
            name={"entity.nif"}
            value={formikValues.entity.nif}
            maxLength={9}
            isInvalid={errors.entity?.nif}
            errorMessage={errors.entity?.nif}
            disabled={nonGetDisabled}
          />
        </Col>
      </Row>

      <Row>
        <Col md="4">
          <Form.Label>
            <FormattedMessage id="eaAccreditationForm.step1.district" />
          </Form.Label>
          <Form.Select
            name="entity.districtCode"
            value={formikValues.entity.districtCode}
            onChange={(e) =>
              setFieldValue("entity.districtCode", e.target.value)
            }
            disabled={nonGetDisabled}
          >
            <option value="">
              {intl.formatMessage({
                id: "all.select",
                defaultMessage: "Selecionar",
              })}
            </option>
            {(districts || []).map((d) => (
              <option key={d.code} value={d.code}>
                {d.description}
              </option>
            ))}
          </Form.Select>
          {errors.entity?.districtCode && (
            <div className="invalid-feedback d-block">
              {errors.entity?.districtCode}
            </div>
          )}
        </Col>
        <Col md="6">
          <NumberInputField
            labelId={"eaAccreditationForm.step1.nationalMemberYear"}
            name={"entity.nationalMemberYear"}
            handleChange={handleChange}
            value={formikValues.entity.nationalMemberYear}
            maxLength={4}
            isInvalid={errors.entity?.nationalMemberYear}
            errorMessage={errors.entity?.nationalMemberYear}
            disabled={nonGetDisabled}
          />
        </Col>
        {isIEFP && (
          <Col md="2">
            <DecimalInputField
              labelId={"eaAccreditationForm.step1.score"}
              controlId={"entity.score"}
              name={"entity.score"}
              handleChange={handleChange}
              value={formikValues.entity.score}
              placeHolder={0}
              maxValue={100}
              suffixLabel={"%"}
              isInvalid={errors.entity?.score}
              errorMessage={errors.entity?.score}
              disabled={true}
            />
          </Col>
        )}
      </Row>

      <Row>
        <Col md="12">
          <TextInputField
            labelId={"eaAccreditationForm.step1.address"}
            name={"entity.address"}
            handleChange={handleChange}
            value={formikValues.entity.address}
            isInvalid={errors.entity?.address}
            errorMessage={errors.entity?.address}
            disabled={nonGetDisabled}
          />
        </Col>
      </Row>

      <Row className="align-items-end">
        <Col md="4">
          <Form.Label>
            <FormattedMessage id="eaAccreditationForm.step1.postalCode" />
          </Form.Label>
          <div className="d-inline d-flex align-items-center">
            <TextInputField
              name={"entity.postalCode4Digits"}
              handleChange={handleChange}
              value={formikValues.entity.postalCode4Digits}
              isInvalid={errors.entity?.postalCode4Digits}
              errorMessage={errors.entity?.postalCode4Digits}
              maxLength={4}
              disabled={nonGetDisabled}
            />
            <span className="ms-2 me-2">-</span>
            <TextInputField
              name={"entity.postalCode3Digits"}
              handleChange={handleChange}
              value={formikValues.entity.postalCode3Digits}
              isInvalid={errors.entity?.postalCode3Digits}
              errorMessage={errors.entity?.postalCode3Digits}
              maxLength={3}
              disabled={nonGetDisabled}
            />
          </div>
        </Col>
        <Col md="8">
          <TextInputField
            placeholder={intl.formatMessage({
              id: "eaAccreditationForm.step1.location.placeholder",
            })}
            name={"entity.location"}
            handleChange={handleChange}
            value={formikValues.entity.location}
            isInvalid={errors.entity?.location}
            errorMessage={errors.entity?.location}
            disabled={nonGetDisabled}
          />
        </Col>
      </Row>

      <Row>
        <Col md="8">
          <TextInputField
            labelId={"eaAccreditationForm.step1.email"}
            name={"entity.email"}
            value={formikValues.entity.email}
            isInvalid={errors.entity?.email}
            errorMessage={errors.entity?.email}
            disabled={true}
          />
        </Col>
        <Col md="4">
          <NumberInputField
            labelId={"eaAccreditationForm.step1.phone"}
            name={"entity.phone"}
            handleChange={handleChange}
            value={formikValues.entity.phone}
            isInvalid={errors.entity?.phone}
            errorMessage={errors.entity?.phone}
            maxLength={9}
            disabled={nonGetDisabled}
          />
        </Col>
      </Row>

      <Row>
        <Col md="8">
          <Form.Label>
            <FormattedMessage id="eaAccreditationForm.step1.type" />
          </Form.Label>
          <Form.Select
            name="entity.profile"
            value={formikValues.entity.profile}
            onChange={entityTypeChangeHandler}
            disabled={nonGetDisabled}
          >
            <option value="">
              {intl.formatMessage({
                id: "all.select",
                defaultMessage: "Selecionar",
              })}
            </option>
            {entityTypeOptions.map((t) => (
              <option key={t} value={t}>
                {intl.formatMessage({
                  id: `eaAccreditationForm.step1.type.option.${t}`,
                })}
              </option>
            ))}
          </Form.Select>
          {entityTypeOtherOptionActive && (
            <div className="mt-1">
              <TextInputField
                placeholder={intl.formatMessage({
                  id: "eaAccreditationForm.step1.type.option.other.placeholder",
                })}
                name={"entity.profileDescription"}
                handleChange={handleChange}
                value={formikValues.entity.profileDescription}
                isInvalid={errors.entity?.profileDescription}
                errorMessage={errors.entity?.profileDescription}
                disabled={nonGetDisabled}
              />
            </div>
          )}
        </Col>
        <Col md="4">
          <IntInputField
            className="w-50"
            labelId={"eaAccreditationForm.step1.cae"}
            name={"entity.cae"}
            handleChange={(e) => handleChangeCae(e)}
            value={formikValues.entity.cae}
            isInvalid={errors.entity?.cae}
            errorMessage={errors.entity?.cae}
            maxSize={5}
            leadingZero={true}
            disabled={nonGetDisabled}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <Form.Label>
            <FormattedMessage id="eaAccreditationForm.step1.getList" />
            <ToolTip
              message={intl.formatMessage({
                id: "eaAccreditationForm.step1.getList.tooltip",
              })}
            />
          </Form.Label>
          {regionalDelegationsAbbreviations.map((rDAbbv) => (
            <Form.Check
              key={rDAbbv}
              type={"checkbox"}
              label={rDAbbv}
              checked={hasAnyGETFor(rDAbbv) || hasNewGETFor(rDAbbv)}
              onChange={(e) =>
                regionalDelegationSelectionChangeHandler(e, rDAbbv)
              }
              disabled={getDisabled || hasLockedGETFor(rDAbbv)}
              isInvalid={rDAbbv in errors}
            />
          ))}
        </Col>
        <Row>
          <Col>
            <small>
              <FormattedMessage id="eaAccreditationForm.step1.getList.regionalDelegationsLegend" />
            </small>
          </Col>
        </Row>
      </Row>

      {regionalDelegationsAbbreviations
        .filter((rd) => hasAnyGETFor(rd) || hasNewGETFor(rd))
        .map((rd) => {
          const bucket = groupedByRD[rd] || { existing: [], news: [] };

          const allExisting = bucket.existing;
          let displayExisting = [];

          const withPos = allExisting.map((e, pos) => ({ ...e, _pos: pos }));
          if (withPos.length > 0) {
            const currentCandidates = withPos.filter(
              (e) => e.item.active !== false
            );
            const currentEntry =
              currentCandidates[currentCandidates.length - 1] ||
              withPos[withPos.length - 1];

            if (currentEntry) {
              displayExisting.push({ ...currentEntry });
            }
          }

          return (
            <div key={`rd-block-${rd}`} className="mb-3">
              <Accordion>
                {displayExisting.map(({ item, index: exIdx }) => {
                  const isInactive = item.active === false;
                  const replacedInDraft =
                    !readMode && hasReplacementGet(item, exIdx);

                  const headerId =
                    "eaAccreditationForm.step4.accordion.function.get";

                  const newMatchIndex = bucket.news.findIndex(
                    (n) => (n.item.anchorIndex ?? null) === exIdx
                  );
                  const newMatch =
                    newMatchIndex >= 0 ? bucket.news[newMatchIndex] : null;

                  return (
                    <React.Fragment key={`ex-${exIdx}`}>
                      <Accordion.Item eventKey={`get-${exIdx}`}>
                        <Accordion.Header>
                          <span className="d-flex align-items-center">
                            <FormattedMessage id={headerId} />
                            {" - " + rd}
                            {!isInactive && replacedInDraft && !readMode && (
                              <span className="badge bg-secondary ms-2">
                                <FormattedMessage
                                  id="eaAccreditationForm.step1.get.toBeReplaced"
                                  defaultMessage="A ser substituído"
                                />
                              </span>
                            )}
                          </span>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="mb-3">
                            <Col md="12">
                              <div className="text-muted">
                                <FormattedMessage id="eaAccreditationForm.step1.getList.name" />
                              </div>
                              <div>{item.name || "-"}</div>
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col md="6">
                              <div className="text-muted">
                                <FormattedMessage id="eaAccreditationForm.step1.getList.phone" />
                              </div>
                              <div>{item.phone || "-"}</div>
                            </Col>
                            <Col md="6">
                              <div className="text-muted">
                                <FormattedMessage id="eaAccreditationForm.step1.getList.mobile" />
                              </div>
                              <div>{item.mobile || "-"}</div>
                            </Col>
                          </Row>
                          <Row>
                            <Col md="12">
                              <div className="text-muted">Email</div>
                              <div>{item.email || "-"}</div>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {!readMode &&
                        (newMatch ? (
                          <Accordion.Item eventKey={`new-${exIdx}`}>
                            <Accordion.Header>
                              <span className="d-flex align-items-center">
                                {errors.getList?.[newMatch.index] &&
                                  (Object.keys(errors.getList[newMatch.index])
                                    .length > 1 ||
                                    !errors.getList[
                                      newMatch.index
                                    ].hasOwnProperty(
                                      "qualificationLevelCode"
                                    )) && (
                                    <FaExclamationCircle className="me-2" />
                                  )}
                                <FormattedMessage
                                  id="eaAccreditationForm.step1.accordion.get.new"
                                  defaultMessage="Novo Gestor da Equipa Tecnica"
                                />
                                &nbsp;-&nbsp;{rd}
                              </span>
                            </Accordion.Header>
                            <Accordion.Body>
                              <Row>
                                <Col>
                                  <TextInputField
                                    labelId={
                                      "eaAccreditationForm.step1.getList.name"
                                    }
                                    name={`getList[${newMatch.index}].name`}
                                    handleChange={handleChange}
                                    value={
                                      formikValues.getList[newMatch.index].name
                                    }
                                    isInvalid={
                                      errors.getList?.[newMatch.index]?.name
                                    }
                                    errorMessage={
                                      errors.getList?.[newMatch.index]?.name
                                    }
                                    disabled={false}
                                  />
                                </Col>
                              </Row>

                              <Row>
                                <Col md="6">
                                  <Form.Label>
                                    <FormattedMessage id="eaAccreditationForm.step1.accordion.header.academicQualification" />
                                  </Form.Label>
                                  <Form.Select
                                    name={`getList[${newMatch.index}].qualificationLevelCode`}
                                    value={
                                      formikValues.getList[newMatch.index]
                                        .qualificationLevelCode
                                    }
                                    onChange={handleChange}
                                  >
                                    <option value="-1">
                                      {intl.formatMessage({
                                        id: "all.select",
                                        defaultMessage: "Selecionar",
                                      })}
                                    </option>
                                    {qlOptions.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  {errors.getList?.[newMatch.index]
                                    ?.qualificationLevelCode && (
                                    <div className="invalid-feedback d-block">
                                      {
                                        errors.getList?.[newMatch.index]
                                          ?.qualificationLevelCode
                                      }
                                    </div>
                                  )}
                                </Col>
                                <Col md="6">
                                  <TextInputField
                                    labelId={
                                      "eaAccreditationForm.step1.getList.email"
                                    }
                                    name={`getList[${newMatch.index}].email`}
                                    handleChange={handleChange}
                                    value={
                                      formikValues.getList[newMatch.index].email
                                    }
                                    isInvalid={
                                      errors.getList?.[newMatch.index]?.email
                                    }
                                    errorMessage={
                                      errors.getList?.[newMatch.index]?.email
                                    }
                                    disabled={false}
                                  />
                                </Col>
                              </Row>

                              <Row>
                                <Col>
                                  <NumberInputField
                                    labelId={
                                      "eaAccreditationForm.step1.getList.phone"
                                    }
                                    name={`getList[${newMatch.index}].phone`}
                                    handleChange={handleChange}
                                    value={
                                      formikValues.getList[newMatch.index].phone
                                    }
                                    isInvalid={
                                      errors.getList?.[newMatch.index]?.phone
                                    }
                                    errorMessage={
                                      errors.getList?.[newMatch.index]?.phone
                                    }
                                    maxLength={9}
                                    disabled={false}
                                  />
                                </Col>
                                <Col>
                                  <NumberInputField
                                    labelId={
                                      "eaAccreditationForm.step1.getList.mobile"
                                    }
                                    name={`getList[${newMatch.index}].mobile`}
                                    handleChange={handleChange}
                                    value={
                                      formikValues.getList[newMatch.index]
                                        .mobile
                                    }
                                    isInvalid={
                                      errors.getList?.[newMatch.index]?.mobile
                                    }
                                    errorMessage={
                                      errors.getList?.[newMatch.index]?.mobile
                                    }
                                    maxLength={9}
                                    disabled={false}
                                  />
                                </Col>
                              </Row>

                              <Row className="mt-2">
                                <Col>
                                  <Form.Check
                                    type="checkbox"
                                    id={`getList[${newMatch.index}].managementExperience`}
                                    name={`getList[${newMatch.index}].managementExperience`}
                                    label={intl.formatMessage({
                                      id: "eaAccreditationForm.step1.getList.managementExperience",
                                      defaultMessage:
                                        "Tem experiencia em gestao ou direcao de projetos/equipas?",
                                    })}
                                    checked={
                                      !!formikValues.getList[newMatch.index]
                                        .managementExperience
                                    }
                                    onChange={handleChange}
                                  />
                                </Col>
                              </Row>

                              <Row>
                                <Col className="mt-2">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0"
                                    onClick={() => cancelNewGET(rd, exIdx)}
                                  >
                                    <FormattedMessage
                                      id="all.cancel"
                                      defaultMessage="Cancelar"
                                    />
                                  </button>
                                </Col>
                              </Row>
                            </Accordion.Body>
                          </Accordion.Item>
                        ) : (
                          <div className="mt-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() => addGET(rd, { anchorIndex: exIdx })}
                              disabled={hasNewGETForAnchor(rd, exIdx)}
                            >
                              {intl.formatMessage({
                                id: "eaAccreditationForm.step1.replaceManager",
                                defaultMessage: "Substituir Gestor",
                              })}
                            </button>
                          </div>
                        ))}
                    </React.Fragment>
                  );
                })}

                {bucket.news
                  .filter((n) => n.item.anchorIndex == null)
                  .map(({ item, index: nIdx }) => (
                    <Accordion.Item
                      eventKey={`new-only-${rd}-${nIdx}`}
                      key={`new-only-${rd}-${nIdx}`}
                    >
                      <Accordion.Header>
                        <span className="d-flex align-items-center">
                          <FormattedMessage
                            id="eaAccreditationForm.step1.accordion.get.new"
                            defaultMessage="Novo Gestor da Equipa Tecnica"
                          />
                          &nbsp;-&nbsp;{rd}
                        </span>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Row>
                          <Col>
                            <TextInputField
                              labelId={"eaAccreditationForm.step1.getList.name"}
                              name={`getList[${nIdx}].name`}
                              handleChange={handleChange}
                              value={formikValues.getList[nIdx].name}
                              isInvalid={errors.getList?.[nIdx]?.name}
                              errorMessage={errors.getList?.[nIdx]?.name}
                              disabled={false}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md="6">
                            <Form.Label>
                              <FormattedMessage id="eaAccreditationForm.step1.accordion.header.academicQualification" />
                            </Form.Label>
                            <Form.Select
                              name={`getList[${nIdx}].qualificationLevelCode`}
                              value={
                                formikValues.getList[nIdx]
                                  .qualificationLevelCode
                              }
                              onChange={handleChange}
                            >
                              <option value="-1">
                                {intl.formatMessage({
                                  id: "all.select",
                                  defaultMessage: "Selecionar",
                                })}
                              </option>
                              {qlOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </Form.Select>
                            {errors.getList?.[nIdx]?.qualificationLevelCode && (
                              <div className="invalid-feedback d-block">
                                {errors.getList?.[nIdx]?.qualificationLevelCode}
                              </div>
                            )}
                          </Col>
                          <Col md="6">
                            <TextInputField
                              labelId={
                                "eaAccreditationForm.step1.getList.email"
                              }
                              name={`getList[${nIdx}].email`}
                              handleChange={handleChange}
                              value={formikValues.getList[nIdx].email}
                              isInvalid={errors.getList?.[nIdx]?.email}
                              errorMessage={errors.getList?.[nIdx]?.email}
                              disabled={false}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col>
                            <NumberInputField
                              labelId={
                                "eaAccreditationForm.step1.getList.phone"
                              }
                              name={`getList[${nIdx}].phone`}
                              handleChange={handleChange}
                              value={formikValues.getList[nIdx].phone}
                              isInvalid={errors.getList?.[nIdx]?.phone}
                              errorMessage={errors.getList?.[nIdx]?.phone}
                              maxLength={9}
                              disabled={false}
                            />
                          </Col>
                          <Col>
                            <NumberInputField
                              labelId={
                                "eaAccreditationForm.step1.getList.mobile"
                              }
                              name={`getList[${nIdx}].mobile`}
                              handleChange={handleChange}
                              value={formikValues.getList[nIdx].mobile}
                              isInvalid={errors.getList?.[nIdx]?.mobile}
                              errorMessage={errors.getList?.[nIdx]?.mobile}
                              maxLength={9}
                              disabled={false}
                            />
                          </Col>
                        </Row>

                        <Row className="mt-2">
                          <Col>
                            <Form.Check
                              type="checkbox"
                              id={`getList[${nIdx}].managementExperience`}
                              name={`getList[${nIdx}].managementExperience`}
                              label={intl.formatMessage({
                                id: "eaAccreditationForm.step1.getList.managementExperience",
                                defaultMessage:
                                  "Tem experiencia em gestao ou direcao de projetos/equipas?",
                              })}
                              checked={
                                !!formikValues.getList[nIdx]
                                  .managementExperience
                              }
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col className="mt-2">
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={() => cancelNewGET(rd, null)}
                            >
                              <FormattedMessage
                                id="all.cancel"
                                defaultMessage="Cancelar"
                              />
                            </button>
                          </Col>
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
              </Accordion>
            </div>
          );
        })}

      <div className="form-actions mt-5">
        <Link to={"/"}>
          <button className="btn btn-link" type="button">
            <FormattedMessage id="all.back" />
          </button>
        </Link>
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleNextStep}
        >
          <FormattedMessage id="all.nextStep" />
        </button>
      </div>
    </div>
  );
}
